import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Reveal, Label } from "@/components/motion";
import { ARTICLES, IMAGES, useTitle } from "@/data/site";

const CATEGORIES = ["Destinations", "Travel Inspiration", "Culture", "Experiences", "Travel Guides"];

export default function Journal() {
  useTitle("The Urban Trail Journal | Urban Trail");
  const [lead, ...rest] = ARTICLES;

  return (
    <div data-testid="journal-page">
      <PageHero
        image={IMAGES.journalHero}
        label="Journal"
        titleLines={["The Urban Trail", "Journal"]}
        sub="Travel stories, destination guides and inspiration for journeys worth remembering."
        tall
      />

      <section className="border-b border-mist bg-white">
        <div className="mx-auto flex max-w-[1600px] flex-wrap gap-x-10 gap-y-4 px-6 py-8 lg:px-12">
          {CATEGORIES.map((c) => (
            <span key={c} data-testid={`journal-category-${c.toLowerCase().replace(/ /g, "-")}`} className="label text-steel">
              {c}
            </span>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1600px] px-6 py-24 lg:px-12 lg:py-36">
          <Reveal>
            <Link to={`/journal/${lead.slug}`} data-testid={`journal-lead-${lead.slug}`} className="group block">
              <div className="overflow-hidden">
                <img
                  src={lead.hero}
                  alt={lead.title}
                  loading="lazy"
                  className="aspect-[21/9] w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
              <div className="mt-10 grid gap-6 lg:grid-cols-12">
                <div className="lg:col-span-8">
                  <p className="label text-gold">{lead.category}</p>
                  <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight text-ink sm:text-5xl">
                    {lead.title}
                  </h2>
                </div>
                <div className="lg:col-span-4">
                  <p className="text-base leading-relaxed text-steel">{lead.excerpt}</p>
                  <span className="link-arrow mt-6 text-ink transition-colors group-hover:text-gold">
                    Read Story <span aria-hidden>→</span>
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>

          <div className="mt-24 grid gap-x-10 gap-y-20 sm:grid-cols-2">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.1}>
                <Link to={`/journal/${a.slug}`} data-testid={`journal-card-${a.slug}`} className="group block">
                  <div className="overflow-hidden">
                    <img
                      src={a.hero}
                      alt={a.title}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <p className="label mt-7 text-gold">{a.category}</p>
                  <h3 className="mt-3 font-serif text-3xl font-light leading-tight tracking-tight text-ink">
                    {a.title}
                  </h3>
                  <p className="mt-3 max-w-md text-[15px] leading-relaxed text-steel">{a.excerpt}</p>
                  <span className="link-arrow mt-5 text-ink transition-colors group-hover:text-gold">
                    Read Story <span aria-hidden>→</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-28 border-t border-mist pt-12 text-center">
              <p className="font-serif text-2xl font-light italic text-steel">
                More stories are being written.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
